import { apiClient } from './client';

// Common filter params for dashboard endpoints
export interface DashboardFilterParams {
  productIds?: string[];
  customerIds?: string[];
}

// ABC Analysis specific params
export interface ABCAnalysisParams extends DashboardFilterParams {
  mode?: 'revenue' | 'margin';
  thresholdA?: number;
  thresholdB?: number;
}

// ABC Analysis response types
export interface ABCProduct {
  id: string;
  name: string;
  code: string;
  category: string;
  quantity: number;
  revenue: number;
  margin: number;
  metricValue: number;
  contribution: number;
  cumulativeContribution: number;
  class: 'A' | 'B' | 'C';
}

export interface ABCClassDistribution {
  class: string;
  count: number;
  revenue: number;
  margin: number;
  contribution: number;
  label: string;
}

export interface ABCClassBreakdown {
  count: number;
  totalRevenue: number;
  totalMargin: number;
  contributionPercent: number;
  products: ABCProduct[];
}

export interface ABCAnalysisResponse {
  config: {
    mode: 'revenue' | 'margin';
    thresholdA: number;
    thresholdB: number;
    totalProducts: number;
    totalRevenue: number;
    totalMargin: number;
  };
  products: ABCProduct[];
  summary: {
    totalProducts: number;
    classA: number;
    classB: number;
    classC: number;
    classAContribution: number;
    classBContribution: number;
    classCContribution: number;
  };
  distribution: ABCClassDistribution[];
  classBreakdown: {
    A: ABCClassBreakdown;
    B: ABCClassBreakdown;
    C: ABCClassBreakdown;
  };
}

export interface ManagedReport {
  id: string;
  name: string;
  description?: string;
  type: 'line' | 'bar' | 'pie' | 'area' | 'table';
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface ManagedReportData {
  data: Array<Record<string, unknown>>;
  summary?: Record<string, unknown>;
}

/** A route or city / area on the dashboard, ranked by quantity. */
export interface RegionRow {
  id: string;
  name: string;
  code: string;
  quantity: number;
  /** Bill value. */
  revenue: number;
  /** Share of all units sold. */
  percentage: number;
  rank: number;
}

export const reportService = {
  // Dashboard-specific methods
  async getDashboardStats(filters?: DashboardFilterParams): Promise<{
    /** Null when no forecast could be compared with sales. */
    forecastAccuracy: number | null;
    /** Points against the previous window of the same length; null when either has no accuracy. */
    accuracyChange: number | null;
    activePlans: number;
    /** Plans locked against changes. */
    lockedPlans: number;
    totalForecasts: number;
    /** Forecasts created in the last three months. */
    forecastsLastQuarter: number;
    /** The newest data import or Marg sync; null when there has been none. */
    lastDataSync: string | null;
  }> {
    const { data } = await apiClient.get('/reports/dashboard/stats', { params: filters });
    return data.data;
  },

  async getModelAccuracy(filters?: DashboardFilterParams): Promise<{ model: string; mape: number }[]> {
    const { data } = await apiClient.get('/reports/dashboard/model-accuracy', { params: filters });
    return data.data;
  },

  // Enterprise dashboard methods
  async getRevenueMetrics(filters?: DashboardFilterParams): Promise<{
    currentMonth: number;
    lastMonth: number;
    momChange: number;
    yoyChange: number;
    ytdRevenue: number;
    ytdForecast: number;
    ytdVariance: number;
  }> {
    const { data } = await apiClient.get('/reports/dashboard/revenue', { params: filters });
    return data.data;
  },

  /** Ranked by quantity; `percentage` is the share of all units sold in the period. */
  async getTopProducts(params?: { limit?: number } & DashboardFilterParams): Promise<
    {
      id: string;
      name: string;
      code: string;
      quantity: number;
      revenue: number;
      percentage: number;
    }[]
  > {
    const { data } = await apiClient.get('/reports/dashboard/top-products', { params });
    return data.data;
  },

  /** Routes ranked by quantity; `percentage` is the share of all units. */
  async getStateBreakdown(filters?: DashboardFilterParams): Promise<RegionRow[]> {
    const { data } = await apiClient.get('/reports/dashboard/regional/state', { params: filters });
    return data.data;
  },

  /** Cities / areas ranked by quantity; `percentage` is the share of all units. */
  async getCityBreakdown(filters?: DashboardFilterParams): Promise<RegionRow[]> {
    const { data } = await apiClient.get('/reports/dashboard/regional/city', { params: filters });
    return data.data;
  },

  async getVarianceAlerts(filters?: DashboardFilterParams): Promise<
    {
      id: string;
      type: 'over' | 'under';
      entity: string;
      period: string;
      expected: number;
      actual: number;
      variance: number;
      severity: 'high' | 'medium' | 'low';
    }[]
  > {
    const { data } = await apiClient.get('/reports/dashboard/variance-alerts', { params: filters });
    return data.data;
  },

  /** The share of products that have a forecast. */
  async getForecastHealth(filters?: DashboardFilterParams): Promise<{ coverage: number }> {
    const { data } = await apiClient.get('/reports/dashboard/forecast-health', { params: filters });
    return data.data;
  },

  // =====================
  // Enhanced Enterprise Methods with Flexible Periods
  // =====================

  async getTrendComparison(
    params: {
      granularity: 'daily' | 'weekly' | 'monthly' | 'quarterly';
      periods?: number;
      startDate?: string;
      endDate?: string;
    } & DashboardFilterParams,
  ): Promise<{
    data: {
      period: string;
      label: string;
      actual: number;
      forecast: number;
      variance: number;
      variancePercent: number;
    }[];
    meta: {
      granularity: string;
      startDate: string;
      endDate: string;
      totalPeriods: number;
    };
  }> {
    const { data } = await apiClient.get('/reports/dashboard/monthly-trend', { params });
    return data;
  },

  async getForecastBias(filters?: DashboardFilterParams): Promise<{
    data: {
      model: string;
      avgBias: number;
      overForecastRate: number;
      underForecastRate: number;
      totalForecasts: number;
    }[];
  }> {
    const { data } = await apiClient.get('/reports/dashboard/forecast-bias', { params: filters });
    return data;
  },

  async getABCAnalysis(params?: ABCAnalysisParams): Promise<ABCAnalysisResponse> {
    const { data } = await apiClient.get('/reports/dashboard/abc-analysis', { params });
    return data;
  },

  // CRUD methods for page-managed reports
  async listReports(): Promise<ManagedReport[]> {
    const { data } = await apiClient.get<ManagedReport[]>('/reports');
    return data;
  },

  async getReportData(id: string): Promise<ManagedReportData> {
    const { data } = await apiClient.get<ManagedReportData>(`/reports/${id}/data`);
    return data;
  },

  async createReport(reportData: Record<string, unknown>): Promise<ManagedReport> {
    const { data } = await apiClient.post<ManagedReport>('/reports', reportData);
    return data;
  },

  async updateReport(id: string, reportData: Record<string, unknown>): Promise<ManagedReport> {
    const { data } = await apiClient.patch<ManagedReport>(`/reports/${id}`, reportData);
    return data;
  },

  async deleteReport(id: string): Promise<void> {
    await apiClient.delete(`/reports/${id}`);
  },
};
